/**
 * Mediation Wall API — Phase 7 M3 (neutral workspace client).
 *
 * TypeScript client for the TWO read surfaces a neutral (mediator org) may hit.
 * The shapes mirror the backend ALLOW-LIST exactly (mediations.py projections +
 * war_room_mediation_neutral_view.build_mediation_neutral_view). The neutral
 * NEVER receives disclosed_by / source_run_id / firm_org_id / case_ref / audit /
 * any engine output — those keys are absent by construction server-side, so they
 * are deliberately not even present in these types.
 *
 * Contract: docs/plans/2026-10-11-phase7-mediation-neutral-view-contract.md
 */
import { api } from '../utils/api';

const BASE = '/api/v1/mediations';

// ── GET /mediations — meta list (pre-/at-active; NO disclosures) ──────────────
export interface MediationConsentState {
  party_id?: string;
  state: 'pending' | 'granted' | 'revoked' | string;
}
export interface MediationMetaParty {
  party_id: string;
  side_label: string;
  represented_party_name: string;
}
export interface MediationListItem {
  id: string;
  title: string;
  status: 'proposed' | 'active' | 'suspended' | 'closed' | 'cancelled' | string;
  parties: MediationMetaParty[];
  consents: MediationConsentState[];
}

// ── GET /mediations/{id}/neutral-view — the allow-listed reconciliation view ──
export interface NeutralViewParty {
  side_label: string;
  represented_party_name: string;
  disclosed_demand: number | null;
  disclosed_demand_basis: string | null;
  stipulated_facts: string[];
  disclosed_at: string | null;
  disclosed: boolean;
}
export interface Reconciliation {
  claimant_position: number | null;
  respondent_position: number | null;
  /** |N_p − N_d| — the divergence band to bridge. NEVER a midpoint/average. */
  spread: number | null;
  basis_labels: { claimant: string | null; respondent: string | null };
  status: 'both_disclosed' | 'awaiting_disclosure' | string;
  caveats: string[];
}
export interface MediationNeutralView {
  id: string;
  title: string;
  status: string;
  parties: NeutralViewParty[];
  consents: Array<{ state: string }>;
  reconciliation: Reconciliation;
  /** Transparency: what the platform deliberately does NOT show the neutral. */
  excluded: string[];
}

/** List the neutral's mediations (meta only — no disclosures). */
export function listMediations(): Promise<{ mediations: MediationListItem[] }> {
  return api.get<{ mediations: MediationListItem[] }>(BASE, { showErrorToast: false });
}

/** The full neutral view — only available once both parties have consented
 *  (the backend gate 403s otherwise). */
export function getNeutralView(mediationId: string): Promise<MediationNeutralView> {
  return api.get<MediationNeutralView>(`${BASE}/${encodeURIComponent(mediationId)}/neutral-view`, {
    showErrorToast: false,
  });
}
